// Lab, radiology and pharmacy orders for the patients OT has today.
//
// OT does not have an ordering system and must not grow one. This screen is a
// case list and a door: pick a case, and the IPD doctor console's own order
// drawer opens against that case's admission. Same orders, same lab and
// radiology dashboards, same pharmacy — OT is just another way in.
//
// WHY THE CASE MUST HAVE AN ADMISSION. Every read and every write in that
// drawer is scoped by admission_id, and an investigation is refused without the
// admission's care-context reference besides. A case with no admission is still
// listed — it is a real case and the coordinator needs to see it — but it
// cannot be opened, and the row says why rather than failing at Sign & Send.

import React, { useCallback, useEffect, useState } from "react";
import { Box, Typography } from "@mui/material";

import {
  FILTER_TOOLBAR,
  IPD_COLORS,
  IpdDataGrid,
  IpdDateField,
  IpdFormField,
  IpdTextField,
  toolbarSx,
} from "../../ipd/utils";
import OrderManagementDrawer from "../../ipd/pages/doctor-console-ipd/OrderManagementDrawer";
import { fetchCase, fetchCases } from "../api/otApi";
import OtScreen from "../components/OtScreen";
import { LifecycleChip, TrackingChip, errorMessage, report } from "../components/common";

/** Today, in the shape <input type="date"> wants. */
function today() {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 10);
}

// ONE DAY, AS THE SERVER READS IT.
//
// The case list filters on an ACTIVE booking's start_datetime, and the upper
// bound is `lte`. Sending the same bare date as both bounds is therefore a
// zero-width window at midnight, which matches nothing — the list would be
// empty every day of the year.
//
// The offset is explicit because the server makes a NAIVE datetime aware in its
// own timezone, and that is UTC, not IST. An IST day sent naive is a day shifted
// five and a half hours: a case booked at 20:00 IST would fall out of its own
// day. `parse_dt` passes an aware value through untouched, which is why saying
// +05:30 here is the reading that survives.
const IST = "+05:30";
const dayStart = (date) => `${date}T00:00:00${IST}`;
const dayEnd = (date) => `${date}T23:59:59${IST}`;

/**
 * The drawer's patient object, out of a full OT case.
 *
 * The drawer reads `admissionId` for every fetch, `careContextRef` before it
 * will send an investigation, and the name/uhid for its header. OT keeps the
 * UHID in `health_record_id` — that column means something different in
 * Emergency, so it is mapped here rather than assumed downstream.
 */
export function drawerPatient(otCase) {
  if (!otCase || !otCase.admission_id) return null;
  return {
    admissionId: otCase.admission_id,
    id: otCase.admission_id,
    patient_id: otCase.patient_id,
    patientId: otCase.patient_id,
    name: otCase.patient_name,
    patient_name: otCase.patient_name,
    uhid: otCase.health_record_id,
    careContextRef: otCase.care_context_ref || "",
    doctor_name: otCase.primary_surgeon_name || "",
  };
}

export default function OtOrders() {
  const [date, setDate] = useState(today());
  const [search, setSearch] = useState("");
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [opening, setOpening] = useState(null);
  const [patient, setPatient] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const body = await fetchCases({
        from: dayStart(date),
        to: dayEnd(date),
        search,
      });
      setRows(body.data || []);
      setCount(body.count || 0);
    } catch (err) {
      setError(errorMessage(err, "Could not load the case list"));
    } finally {
      setLoading(false);
    }
  }, [date, search]);

  useEffect(() => {
    const handle = window.setTimeout(load, 300);
    return () => window.clearTimeout(handle);
  }, [load]);

  // The list serializer does not carry the care-context reference — it is a
  // per-row admission query the worklist must not pay. So the full case is
  // fetched on the way into the drawer, which is the one moment it is needed.
  const open = async (row) => {
    setOpening(row.id);
    try {
      const full = await fetchCase(row.id);
      const built = drawerPatient(full);
      if (!built) {
        // `report` keys on envelope.ok, so an envelope without it is a failure
        // and its message is what gets toasted.
        report({ message: "This case is not linked to an admission." });
        return;
      }
      setPatient(built);
    } catch (err) {
      report({ message: errorMessage(err, "Could not open the case") });
    } finally {
      setOpening(null);
    }
  };

  const columns = [
    { field: "case_number", headerName: "Case", width: 140 },
    { field: "patient_name", headerName: "Patient", flex: 1, minWidth: 160 },
    { field: "health_record_id", headerName: "UHID", width: 130 },
    {
      field: "procedure",
      headerName: "Procedure",
      flex: 1,
      minWidth: 170,
      sortable: false,
      valueGetter: (p) => p.row.procedures?.find((x) => x.is_primary)?.procedure_name || "—",
    },
    { field: "primary_surgeon_name", headerName: "Surgeon", width: 150 },
    {
      field: "lifecycle_status",
      headerName: "Status",
      width: 130,
      sortable: false,
      renderCell: (p) => <LifecycleChip status={p.row.lifecycle_status} />,
    },
    {
      field: "tracking_status",
      headerName: "Board",
      width: 150,
      sortable: false,
      renderCell: (p) => <TrackingChip status={p.row.tracking_status} />,
    },
    {
      field: "orders",
      headerName: "Orders",
      width: 190,
      sortable: false,
      renderCell: (p) =>
        p.row.admission_id ? (
          <Typography
            component="button"
            type="button"
            onClick={() => open(p.row)}
            disabled={opening === p.row.id}
            sx={{
              border: 0,
              background: "none",
              cursor: "pointer",
              p: 0,
              fontSize: 13,
              fontWeight: 700,
              color: IPD_COLORS.primary,
            }}
          >
            {opening === p.row.id ? "Opening…" : "Place orders"}
          </Typography>
        ) : (
          <Typography sx={{ fontSize: 12, color: IPD_COLORS.warning.text }}>
            No admission
          </Typography>
        ),
    },
  ];

  return (
    <OtScreen
      title="Orders"
      description="Lab, radiology and pharmacy orders for the patients on today's list."
      badge={count}
      loading={loading}
      error={error}
      onRetry={load}
    >
      <Box sx={{ ...toolbarSx, mb: FILTER_TOOLBAR.mb }}>
        <Box sx={{ width: FILTER_TOOLBAR.filterWidthWide }}>
          <IpdFormField label="Date">
            {/* IpdDateField emits a bare ISO string, not an event. */}
            <IpdDateField value={date} onChange={(iso) => setDate(iso || "")} />
          </IpdFormField>
        </Box>
        <Box sx={{ minWidth: FILTER_TOOLBAR.searchMin, maxWidth: FILTER_TOOLBAR.searchMax, flex: 1 }}>
          <IpdFormField label="Patient or case">
            <IpdTextField
              fullWidth
              placeholder="Name, UHID or case number"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </IpdFormField>
        </Box>
      </Box>

      <IpdDataGrid
        rows={rows}
        columns={columns}
        getRowId={(row) => row.id}
        fillHeight
        emptyMessage="No cases on this date"
        emptyDescription="Change the date, or clear the search."
      />

      {patient && (
        <OrderManagementDrawer
          open
          onClose={() => setPatient(null)}
          patient={patient}
        />
      )}
    </OtScreen>
  );
}
